import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs as MantineBreadcrumbs, Anchor, Text } from '@mantine/core';
import { BreadcrumbsProps, BreadcrumbItem } from './Breadcrumbs.types';

/**
 * Breadcrumbs Component
 *
 * Provides navigation breadcrumb trail for hierarchical pages
 *
 * Features:
 * - Click to navigate to parent pages
 * - Current page is not clickable
 * - Responsive design
 *
 * Usage:
 * ```tsx
 * <Breadcrumbs
 *   items={[
 *     { label: 'Regions', href: '/admin/regions' },
 *     { label: 'Seattle Metro', href: '/admin/regions/seattle-id' },
 *     { label: 'SubAreas' }
 *   ]}
 * />
 * ```
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }): React.ReactElement => {
  const navigate = useNavigate();

  /**
   * Handle breadcrumb click
   */
  const handleClick = (item: BreadcrumbItem): void => {
    if (item.href) {
      navigate(item.href);
    }
  };

  return (
    <MantineBreadcrumbs separator="›">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        // Last item (current page) - not clickable
        if (isLast || !item.href) {
          return (
            <Text key={index} size="sm" c="dimmed">
              {item.label}
            </Text>
          );
        }

        // Clickable breadcrumb
        return (
          <Anchor
            key={index}
            size="sm"
            onClick={() => handleClick(item)}
            style={{ cursor: 'pointer' }}
          >
            {item.label}
          </Anchor>
        );
      })}
    </MantineBreadcrumbs>
  );
};
